import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../../config/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import getBackendUrl from '../../config/backend';
import './DentalTriagePage.css';

const DentalTriagePage = () => {


  const [form, setForm] = useState({
    name: '',
    email: '',
    mainSymptom: '',
    painLevel: 0,
    painOnset: '',
    painLocation: '',
    swelling: 'No',
    swellingType: '',
    fever: 'No',
    mouthOpeningNormal: 'Yes',
    bleeding: 'No',
    bleedingAfterExtraction: 'No',
    bleedingControllable: 'Yes',
    trauma: 'No',
    previousEpisodes: 'No',
    photo: null,
    // Advanced optional fields
    recentExtraction: 'No',
    daysSinceExtraction: '',
    pulpExposure: 'No',
    percussionTenderness: 'No',
    softTissueTrauma: 'No',
    wirePoking: 'No',
    tmjMusclePain: 'No',
    jawClenching: 'No',
    ulcerDurationDays: '',
    neuropathicFeatures: 'No',
    coldTriggered: 'No',
    heatSensitivity: 'No',
    painOnBiting: 'No',
  });
  
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [lastPhotoName, setLastPhotoName] = useState(null);
  // Removed email modal - leads are captured automatically

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setForm((prev) => ({ ...prev, [name]: files && files[0] ? files[0] : null }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRadio = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDownloadPdf = () => {
    if (!result) return;
    const doc = new jsPDF();
    const marginX = 14;
    let y = 18;
    const line = (text, opts = {}) => {
      const maxWidth = 180;
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, marginX, y, opts);
      y += 6 * lines.length;
    };

    // Heading
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    line('Dental Triage Report');
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    line('Generated by SmileConsent Dental Triage Tool');

    // Date
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    line(`Date: ${new Date().toLocaleString()}`);

    // Divider
    y += 2;
    doc.line(marginX, y, 200 - marginX, y);
    y += 8;

    // Result Section
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    line('Assessment');
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    line(`Provisional Diagnosis: ${result.provisional_diagnosis || '-'}`);
    line(`Secondary Diagnosis: ${result.secondary_diagnosis || '-'}`);
    line(`Urgency Level: ${result.urgency_level || '-'}`);
    line('Recommended Action:');
    line(`${result.recommended_action || '-'}`);

    // Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.text(`Page ${i} of ${pageCount}`, 200 - marginX, 290, { align: 'right' });
    }

    doc.save('smileconsent-dental-triage-result.pdf');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setLoading(true);

    // Simple validation: if recent extraction is Yes, require number of days
    if (form.recentExtraction === 'Yes' && (form.daysSinceExtraction === '' || isNaN(parseInt(form.daysSinceExtraction, 10)))) {
      setLoading(false);
      setError('Please enter how many days ago the tooth was extracted.');
      const el = document.getElementById('daysSinceExtraction');
      if (el) el.focus();
      return;
    }

    // Map UI state (camelCase, Yes/No) to API schema (snake_case, booleans)
    const payload = {
      main_symptom: form.mainSymptom || '',
      pain_level: parseInt(form.painLevel, 10) || 0,
      pain_onset: form.painOnset || '',
      pain_location: form.painLocation || '',
      swelling: form.swelling === 'Yes' ? 'Yes' : (form.swelling === 'No' ? 'No' : form.swelling || null),
      swelling_type: form.swelling === 'Yes' ? (form.swellingType || null) : null,
      fever: form.fever === 'Yes',
      trismus: form.mouthOpeningNormal === 'No',
      bleeding: form.bleeding === 'Yes',
      bleeding_after_extraction: form.bleeding === 'Yes' ? (form.bleedingAfterExtraction === 'Yes') : null,
      bleeding_controlled: form.bleeding === 'Yes' ? (form.bleedingControllable === 'Yes') : null,
      trauma: form.trauma === 'Yes',
      recurrence: form.previousEpisodes === 'Yes',
      // Advanced optional mappings
      recent_extraction: form.recentExtraction === 'Yes' ? true : (form.recentExtraction === 'No' ? false : null),
      days_since_extraction: form.recentExtraction === 'Yes' && form.daysSinceExtraction !== '' ? parseInt(form.daysSinceExtraction, 10) : null,
      pulp_exposure: form.pulpExposure === 'Yes',
      percussion_tenderness: form.percussionTenderness === 'Yes',
      soft_tissue_trauma: form.softTissueTrauma === 'Yes',
      wire_poking: form.wirePoking === 'Yes' ? true : (form.wirePoking === 'No' ? false : null),
      tmj_muscle_pain: form.tmjMusclePain === 'Yes',
      jaw_clenching: form.jawClenching === 'Yes',
      ulcer_duration_days: form.ulcerDurationDays !== '' ? parseInt(form.ulcerDurationDays, 10) : null,
      neuropathic_features: form.neuropathicFeatures === 'Yes',
      cold_triggered: form.coldTriggered === 'Yes',
      heat_sensitivity: form.heatSensitivity === 'Yes',
      pain_on_biting: form.painOnBiting === 'Yes',
    };

    try {
      const backendUrl = getBackendUrl();
      let data;

      if (backendUrl === 'firebase-functions') {
        // Use Firebase Functions in production
        const dentalTriage = httpsCallable(functions, 'dentalTriage');
        const response = await dentalTriage(payload);
        data = response.data;
      } else {
        // Use FastAPI backend in development
        const response = await fetch(`${backendUrl}/triage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        data = await response.json();
      }

      setResult(data);
      setLastSubmission(payload);
      setLastPhotoName(form.photo ? form.photo.name : null);

      // Automatically store name and email for superadmin (no popup)
      if (form.name || form.email) {
        try {
          await addDoc(collection(db, 'triageLeads'), {
            name: form.name || 'Anonymous',
            email: form.email || 'No email provided',
            diagnosis: data.provisional_diagnosis,
            urgency: data.urgency_level,
            timestamp: new Date(),
            symptoms: payload,
            source: 'dental-triage'
          });
          console.log('✅ Lead captured for superadmin');
        } catch (error) {
          console.error('❌ Failed to store lead:', error);
        }
      }

      // Optionally clear the form after success
      setForm({
        name: '',
        email: '',
        mainSymptom: '',
        painLevel: 0,
        painOnset: '',
        painLocation: '',
        swelling: 'No',
        swellingType: '',
        fever: 'No',
        mouthOpeningNormal: 'Yes',
        bleeding: 'No',
        bleedingAfterExtraction: 'No',
        bleedingControllable: 'Yes',
        trauma: 'No',
        previousEpisodes: 'No',
        photo: null,
        recentExtraction: 'No',
        daysSinceExtraction: '',
        pulpExposure: 'No',
        percussionTenderness: 'No',
        softTissueTrauma: 'No',
        wirePoking: 'No',
        tmjMusclePain: 'No',
        jawClenching: 'No',
        ulcerDurationDays: '',
        neuropathicFeatures: 'No',
        coldTriggered: 'No',
        heatSensitivity: 'No',
        painOnBiting: 'No',
      });
    } catch (err) {
      console.error('Failed to submit triage:', err);
      setError(err.message || 'Failed to reach the triage service.');
    } finally {
      setLoading(false);
    }
  };

  // Email submission function removed - leads are captured automatically

  const showSwellingType = form.swelling === 'Yes';
  const showBleedingDetails = form.bleeding === 'Yes';
  const showDaysSinceExtraction = form.recentExtraction === 'Yes';
  const showUlcerDuration = form.mainSymptom === 'Ulceration' || form.ulcerDurationDays !== '';

  // Removed authentication requirement for immediate access
  // if (authLoading) {
  //   return (
  //     <div className="min-h-screen bg-gray-50 flex items-center justify-center">
  //       <div className="text-center">
  //         <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
  //         <p className="text-gray-600">Loading...</p>
  //       </div>
  //     </div>
  //   );
  // }

  // if (!user) {
  //   return <TriageAuth onAuthSuccess={() => {}} />;
  // }

  return (
    <>
      <Helmet>
        <title>Free Dental Triage Tool | UK Emergency Assessment | Smile Consent</title>
        <meta name="description" content="Free dental triage tool for UK patients. Get instant emergency assessment for dental pain, swelling, trauma & urgent symptoms. Professional guidance 24/7." />
        <meta name="keywords" content="dental triage, free dental assessment, dental emergency, dental pain, toothache, dental swelling, UK dental triage, dental trauma, urgent dental care" />
        <link rel="canonical" href="https://smileconsent.co.uk/dentaltriage" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Free Dental Triage Tool | UK Emergency Assessment | Smile Consent" />
        <meta property="og:description" content="Free dental triage tool for UK patients. Get instant emergency assessment for dental pain, swelling, trauma & urgent symptoms. Professional guidance 24/7." />
        <meta property="og:url" content="https://smileconsent.co.uk/dentaltriage" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Dental Triage Tool | UK Emergency Assessment | Smile Consent" />
        <meta name="twitter:description" content="Free dental triage tool for UK patients. Get instant emergency assessment for dental pain, swelling, trauma & urgent symptoms. Professional guidance 24/7." />
        <meta name="twitter:image" content="https://smileconsent.co.uk/og-image.png" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Free Dental Triage Tool",
            "description": "Free dental triage tool for UK patients. Get instant emergency assessment for dental pain, swelling, trauma & urgent symptoms.",
            "url": "https://smileconsent.co.uk/dentaltriage",
            "applicationCategory": "HealthApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "GBP"
            },
            "provider": {
              "@type": "Organization",
              "name": "Smile Consent",
              "url": "https://smileconsent.co.uk",
              "sameAs": [
                "https://smileconsent.co.uk/digital-consent-dentists",
                "https://smileconsent.co.uk/dental-consent-forms",
                "https://smileconsent.co.uk/dental-consent-plans",
                "https://smileconsent.co.uk/dental-compliance-software",
                "https://smileconsent.co.uk/dental-practice-management"
              ]
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-between items-center mb-6">
            <a 
              href="https://smileconsent.co.uk" 
              className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Back to SmileConsent Home
            </a>
            <div></div>
          </div>
          
          {/* SmileConsent Logo */}
          <div className="flex justify-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div className="text-left">
                <div className="text-2xl font-bold text-gray-900">SmileConsent</div>
                <div className="text-sm text-gray-600">Dental Consent Solutions</div>
              </div>
            </div>
          </div>

                  <h1 className="text-4xl font-bold text-gray-900 mb-4">
                    Dental Emergency Triage
                  </h1>
                  <p className="text-xl text-gray-600 mb-2">
                    Professional dental assessment tool by SmileConsent
                  </p>
                  <p className="text-gray-500">
                    Professional guidance for dental teams - No registration required
                  </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Patient Assessment Form</h2>
            <p className="text-gray-600">Please provide details about your dental symptoms for accurate triage assessment.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Professional Information Section */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Professional Information</h3>
                  <p className="text-gray-600">Your details for the assessment report</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-3">
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="Dr. John Smith"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all duration-200 hover:border-gray-300"
                  />
                </div>

                {/* Email */}
                <div className="space-y-3">
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="dr.smith@clinic.com"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all duration-200 hover:border-gray-300"
                  />
                </div>
              </div>
            </div>

            {/* Primary Symptoms Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Primary Symptoms</h3>
                  <p className="text-gray-600">Tell us about your main concern</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Main Symptom */}
                <div className="space-y-3">
                  <label htmlFor="mainSymptom" className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Main Symptom *
                  </label>
                  <select
                    id="mainSymptom"
                    name="mainSymptom"
                    value={form.mainSymptom}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 hover:border-gray-300"
                  >
                    <option value="" disabled>Select a symptom</option>
                    <option value="Pain">🦷 Pain</option>
                    <option value="Swelling">📈 Swelling</option>
                    <option value="Bleeding">🩸 Bleeding</option>
                    <option value="Trauma">⚠️ Trauma</option>
                    <option value="Ulceration">🔴 Ulceration</option>
                    <option value="Abnormal appearance">👁️ Abnormal appearance</option>
                  </select>
                </div>

                {/* Pain Level */}
                <div className="space-y-3">
                  <label htmlFor="painLevel" className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Pain Level: <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-bold ml-2">{form.painLevel}/10</span>
                  </label>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <input
                      type="range"
                      id="painLevel"
                      name="painLevel"
                      min="0"
                      max="10"
                      step="1"
                      value={form.painLevel}
                      onChange={handleChange}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${form.painLevel * 10}%, #e5e7eb ${form.painLevel * 10}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-gray-600 mt-2">
                      <span className="flex items-center">
                        <span className="text-green-600 mr-1">😊</span> 0 - No pain
                      </span>
                      <span className="flex items-center">
                        <span className="text-yellow-600 mr-1">😐</span> 5 - Moderate
                      </span>
                      <span className="flex items-center">
                        <span className="text-red-600 mr-1">😫</span> 10 - Severe
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pain Details Section */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-xl p-6 border border-orange-100">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Pain Details</h3>
                  <p className="text-gray-600">When and where is the pain occurring?</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Pain Onset */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Pain Onset
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="painOnset"
                        value="Sudden"
                        checked={form.painOnset === 'Sudden'}
                        onChange={(e) => handleRadio('painOnset', e.target.value)}
                        className="mr-3 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">⚡</span>
                        <span className="font-medium">Sudden</span>
                        <span className="text-gray-500 text-sm ml-2">- Started quickly</span>
                      </div>
                    </label>
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="painOnset"
                        value="Gradual"
                        checked={form.painOnset === 'Gradual'}
                        onChange={(e) => handleRadio('painOnset', e.target.value)}
                        className="mr-3 text-orange-600 focus:ring-orange-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">📈</span>
                        <span className="font-medium">Gradual</span>
                        <span className="text-gray-500 text-sm ml-2">- Developed slowly</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Pain Location */}
                <div className="space-y-3">
                  <label htmlFor="painLocation" className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Pain Location
                  </label>
                  <select
                    id="painLocation"
                    name="painLocation"
                    value={form.painLocation}
                    onChange={handleChange}
                    className="mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white transition-all duration-200 hover:border-gray-300"
                  >
                    <option value="" disabled>Select location</option>
                    <option value="Tooth">🦷 Tooth</option>
                    <option value="Jaw">🦴 Jaw</option>
                    <option value="Gums">🩸 Gums</option>
                    <option value="Face">👤 Face</option>
                    <option value="Other">📍 Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Symptoms Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Additional Symptoms</h3>
                  <p className="text-gray-600">Tell us about any other symptoms you're experiencing</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Swelling */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2m-10 0v16" />
                    </svg>
                    Swelling
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-green-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="swelling"
                        value="Yes"
                        checked={form.swelling === 'Yes'}
                        onChange={(e) => handleRadio('swelling', e.target.value)}
                        className="mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">📈</span>
                        <span className="font-medium">Yes</span>
                        <span className="text-gray-500 text-sm ml-2">- I have swelling</span>
                      </div>
                    </label>
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-green-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="swelling"
                        value="No"
                        checked={form.swelling === 'No'}
                        onChange={(e) => handleRadio('swelling', e.target.value)}
                        className="mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">✅</span>
                        <span className="font-medium">No</span>
                        <span className="text-gray-500 text-sm ml-2">- No swelling</span>
                      </div>
                    </label>
                  </div>
                </div>

                {showSwellingType && (
                  <div className="space-y-3">
                    <label htmlFor="swellingType" className="block text-sm font-semibold text-gray-700 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2m-10 0v16" />
                      </svg>
                      Swelling Type *
                    </label>
                    <select
                      id="swellingType"
                      name="swellingType"
                      value={form.swellingType}
                      onChange={handleChange}
                      required={showSwellingType}
                      className="mt-1 block w-full px-4 py-3 border-2 border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition-all duration-200 hover:border-gray-300"
                    >
                      <option value="" disabled>Select type</option>
                      <option value="Localised (gums)">🎯 Localised (gums)</option>
                      <option value="Spreading (eye/neck)">⚠️ Spreading (eye/neck)</option>
                    </select>
                  </div>
                )}

                {/* Fever */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Fever or unwell?
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-green-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="fever"
                        value="Yes"
                        checked={form.fever === 'Yes'}
                        onChange={(e) => handleRadio('fever', e.target.value)}
                        className="mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">🌡️</span>
                        <span className="font-medium">Yes</span>
                        <span className="text-gray-500 text-sm ml-2">- I have fever or feel unwell</span>
                      </div>
                    </label>
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-green-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="fever"
                        value="No"
                        checked={form.fever === 'No'}
                        onChange={(e) => handleRadio('fever', e.target.value)}
                        className="mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">✅</span>
                        <span className="font-medium">No</span>
                        <span className="text-gray-500 text-sm ml-2">- No fever, feeling fine</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Medical Assessment Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mr-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Additional Questions</h3>
                  <p className="text-gray-600">Additional questions for accurate triage</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Mouth Opening */}
                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Can you open your mouth normally?
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="mouthOpeningNormal"
                        value="Yes"
                        checked={form.mouthOpeningNormal === 'Yes'}
                        onChange={(e) => handleRadio('mouthOpeningNormal', e.target.value)}
                        className="mr-3 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">✅</span>
                        <span className="font-medium">Yes</span>
                        <span className="text-gray-500 text-sm ml-2">- Mouth opens normally</span>
                      </div>
                    </label>
                    <label className="flex items-center p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-300 cursor-pointer transition-colors">
                      <input
                        type="radio"
                        name="mouthOpeningNormal"
                        value="No"
                        checked={form.mouthOpeningNormal === 'No'}
                        onChange={(e) => handleRadio('mouthOpeningNormal', e.target.value)}
                        className="mr-3 text-purple-600 focus:ring-purple-500"
                      />
                      <div className="flex items-center">
                        <span className="text-2xl mr-2">❌</span>
                        <span className="font-medium">No</span>
                        <span className="text-gray-500 text-sm ml-2">- Limited mouth opening</span>
                      </div>
                    </label>
                  </div>
                </div>

              {/* Bleeding */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Bleeding?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bleeding"
                      value="Yes"
                      checked={form.bleeding === 'Yes'}
                      onChange={(e) => handleRadio('bleeding', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="bleeding"
                      value="No"
                      checked={form.bleeding === 'No'}
                      onChange={(e) => handleRadio('bleeding', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {showBleedingDetails && (
                <>
                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Was it after tooth removal?</span>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bleedingAfterExtraction"
                          value="Yes"
                          checked={form.bleedingAfterExtraction === 'Yes'}
                          onChange={(e) => handleRadio('bleedingAfterExtraction', e.target.value)}
                          className="mr-2"
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bleedingAfterExtraction"
                          value="No"
                          checked={form.bleedingAfterExtraction === 'No'}
                          onChange={(e) => handleRadio('bleedingAfterExtraction', e.target.value)}
                          className="mr-2"
                        />
                        No
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="block text-sm font-medium text-gray-700">Is it controllable with pressure?</span>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bleedingControllable"
                          value="Yes"
                          checked={form.bleedingControllable === 'Yes'}
                          onChange={(e) => handleRadio('bleedingControllable', e.target.value)}
                          className="mr-2"
                        />
                        Yes
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="bleedingControllable"
                          value="No"
                          checked={form.bleedingControllable === 'No'}
                          onChange={(e) => handleRadio('bleedingControllable', e.target.value)}
                          className="mr-2"
                        />
                        No
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Trauma */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Recent trauma?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="trauma"
                      value="Yes"
                      checked={form.trauma === 'Yes'}
                      onChange={(e) => handleRadio('trauma', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="trauma"
                      value="No"
                      checked={form.trauma === 'No'}
                      onChange={(e) => handleRadio('trauma', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Previous Episodes */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Previous episodes?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="previousEpisodes"
                      value="Yes"
                      checked={form.previousEpisodes === 'Yes'}
                      onChange={(e) => handleRadio('previousEpisodes', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="previousEpisodes"
                      value="No"
                      checked={form.previousEpisodes === 'No'}
                      onChange={(e) => handleRadio('previousEpisodes', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Advanced Symptoms Section */}
              <div className="md:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Symptoms (Optional)</h3>
              </div>

              {/* Cold Triggered */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is the pain triggered by cold?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="coldTriggered"
                      value="Yes"
                      checked={form.coldTriggered === 'Yes'}
                      onChange={(e) => handleRadio('coldTriggered', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="coldTriggered"
                      value="No"
                      checked={form.coldTriggered === 'No'}
                      onChange={(e) => handleRadio('coldTriggered', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Heat Sensitivity */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is there heat sensitivity?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="heatSensitivity"
                      value="Yes"
                      checked={form.heatSensitivity === 'Yes'}
                      onChange={(e) => handleRadio('heatSensitivity', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="heatSensitivity"
                      value="No"
                      checked={form.heatSensitivity === 'No'}
                      onChange={(e) => handleRadio('heatSensitivity', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Pain on Biting */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is there pain on biting?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="painOnBiting"
                      value="Yes"
                      checked={form.painOnBiting === 'Yes'}
                      onChange={(e) => handleRadio('painOnBiting', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="painOnBiting"
                      value="No"
                      checked={form.painOnBiting === 'No'}
                      onChange={(e) => handleRadio('painOnBiting', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Recent Extraction */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Had a tooth extracted recently?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recentExtraction"
                      value="Yes"
                      checked={form.recentExtraction === 'Yes'}
                      onChange={(e) => handleRadio('recentExtraction', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="recentExtraction"
                      value="No"
                      checked={form.recentExtraction === 'No'}
                      onChange={(e) => handleRadio('recentExtraction', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {showDaysSinceExtraction && (
                <div className="space-y-2">
                  <label htmlFor="daysSinceExtraction" className="block text-sm font-medium text-gray-700">
                    How many days ago? (2-4 typical for dry socket)
                  </label>
                  <input
                    id="daysSinceExtraction"
                    name="daysSinceExtraction"
                    type="number"
                    min="0"
                    value={form.daysSinceExtraction}
                    onChange={handleChange}
                    placeholder="e.g., 3"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}

              {/* Pulp Exposure */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is the pulp (nerve) visibly exposed?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pulpExposure"
                      value="Yes"
                      checked={form.pulpExposure === 'Yes'}
                      onChange={(e) => handleRadio('pulpExposure', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="pulpExposure"
                      value="No"
                      checked={form.pulpExposure === 'No'}
                      onChange={(e) => handleRadio('pulpExposure', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Percussion Tenderness */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is the tooth tender to biting or tapping?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="percussionTenderness"
                      value="Yes"
                      checked={form.percussionTenderness === 'Yes'}
                      onChange={(e) => handleRadio('percussionTenderness', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="percussionTenderness"
                      value="No"
                      checked={form.percussionTenderness === 'No'}
                      onChange={(e) => handleRadio('percussionTenderness', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Soft Tissue Trauma */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Soft tissue trauma (cut, laceration)?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="softTissueTrauma"
                      value="Yes"
                      checked={form.softTissueTrauma === 'Yes'}
                      onChange={(e) => handleRadio('softTissueTrauma', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="softTissueTrauma"
                      value="No"
                      checked={form.softTissueTrauma === 'No'}
                      onChange={(e) => handleRadio('softTissueTrauma', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Wire Poking */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is your orthodontic appliance poking into your mouth?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wirePoking"
                      value="Yes"
                      checked={form.wirePoking === 'Yes'}
                      onChange={(e) => handleRadio('wirePoking', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wirePoking"
                      value="No"
                      checked={form.wirePoking === 'No'}
                      onChange={(e) => handleRadio('wirePoking', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* TMJ Muscle Pain */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Jaw muscle pain or clenching?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tmjMusclePain"
                      value="Yes"
                      checked={form.tmjMusclePain === 'Yes'}
                      onChange={(e) => handleRadio('tmjMusclePain', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tmjMusclePain"
                      value="No"
                      checked={form.tmjMusclePain === 'No'}
                      onChange={(e) => handleRadio('tmjMusclePain', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Jaw Clenching */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Do you clench your jaw?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="jawClenching"
                      value="Yes"
                      checked={form.jawClenching === 'Yes'}
                      onChange={(e) => handleRadio('jawClenching', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="jawClenching"
                      value="No"
                      checked={form.jawClenching === 'No'}
                      onChange={(e) => handleRadio('jawClenching', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Neuropathic Features */}
              <div className="space-y-2">
                <span className="block text-sm font-medium text-gray-700">Is the pain electric-shock-like or triggered by touch/wind?</span>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="neuropathicFeatures"
                      value="Yes"
                      checked={form.neuropathicFeatures === 'Yes'}
                      onChange={(e) => handleRadio('neuropathicFeatures', e.target.value)}
                      className="mr-2"
                    />
                    Yes
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="neuropathicFeatures"
                      value="No"
                      checked={form.neuropathicFeatures === 'No'}
                      onChange={(e) => handleRadio('neuropathicFeatures', e.target.value)}
                      className="mr-2"
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Ulcer Duration */}
              {showUlcerDuration && (
                <div className="space-y-2">
                  <label htmlFor="ulcerDurationDays" className="block text-sm font-medium text-gray-700">
                    How many days has the ulcer been present?
                  </label>
                  <input
                    id="ulcerDurationDays"
                    name="ulcerDurationDays"
                    type="number"
                    min="0"
                    value={form.ulcerDurationDays}
                    onChange={handleChange}
                    placeholder="e.g., 3"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-center pt-8">
              <button
                type="submit"
                disabled={loading}
                className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-bold py-4 px-12 rounded-xl transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 shadow-lg hover:shadow-xl disabled:shadow-md"
              >
                <div className="flex items-center">
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing Assessment...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-3 group-hover:animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Submit Triage Assessment
                    </>
                  )}
                </div>
              </button>
            </div>
          </form>

          {/* Error Display */}
          {error && (
            <div className="mt-6 bg-red-50 border-l-4 border-red-400 rounded-lg p-6 shadow-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-red-800">Submission Error</h3>
                  <div className="mt-2 text-red-700">
                    <p className="font-medium">{error}</p>
                    <p className="text-sm mt-1">Please check your internet connection and try again.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className="mt-8">
              <TriageResultCard result={result} />
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleDownloadPdf}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition duration-200"
                >
                  Download PDF Report
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500">
          <p>Powered by <span className="font-semibold text-blue-600">SmileConsent</span></p>
          <p className="text-sm mt-2">
            This tool provides guidance only and does not replace professional dental consultation.
          </p>
        </div>
      </div>

      {/* Email modal removed - leads are captured automatically */}
      </div>
    </>
  );
};

// Triage Result Component
const TriageResultCard = ({ result }) => {
  const { provisional_diagnosis, secondary_diagnosis, urgency_level, recommended_action } = result || {};

  const urgency = (urgency_level || '').toLowerCase();
  const getUrgencyColor = () => {
    switch (urgency) {
      case 'emergency':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'urgent':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'non-urgent':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'self-care':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const definitions = {
    'Acute Dental Abscess': 'A painful bacterial infection with pus buildup in the gum or bone.',
    'Cellulitis': 'A serious skin and tissue infection that can spread rapidly.',
    'Pulpitis': 'Inflammation of the dental pulp, often causing intense pain.',
    'Pericoronitis': 'Infection of the gum around a partially erupted tooth.',
    'Post-extraction Haemorrhage': 'Bleeding that continues after tooth removal.',
    'Gingivitis': 'Mild gum inflammation, usually caused by plaque buildup.',
    'Dentine Hypersensitivity': 'Short sharp pain triggered by cold, hot, or sweet foods.',
    'Tooth Avulsion': 'A tooth completely knocked out due to trauma.',
    'Ulceration': 'Painful sore inside the mouth or on the tongue.',
    'Cracked Tooth': 'A fracture in the tooth structure, often painful while chewing.',
    'Possible Trigeminal Neuralgia': 'A nerve-related facial pain condition causing sudden, severe, shock-like pain; requires medical assessment.',
    'Persistent Oral Ulcer (>14 days)': 'An ulcer that has persisted for more than 2 weeks, which requires urgent evaluation to rule out serious conditions including oral cancer.',
    'Possible Squamous Cell Carcinoma': 'A type of oral cancer that can appear as a persistent ulcer or sore. Requires immediate medical evaluation and possible biopsy.',
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-xl font-bold text-gray-900">Assessment Result</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getUrgencyColor()}`}>
          {urgency_level || '—'}
        </span>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center space-x-2 mb-1">
            <h4 className="text-lg font-semibold text-gray-800">Provisional Diagnosis</h4>
            {provisional_diagnosis && definitions[provisional_diagnosis] && (
              <span
                className="text-blue-600 cursor-help"
                title={definitions[provisional_diagnosis]}
              >
                ℹ️
              </span>
            )}
          </div>
          <p className="text-gray-700">{provisional_diagnosis || '-'}</p>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-1">Secondary Diagnosis</h4>
          <p className="text-gray-600 italic">{secondary_diagnosis || '-'}</p>
        </div>

        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-1">Recommended Action</h4>
          <p className="text-gray-700">{recommended_action || '-'}</p>
        </div>
      </div>
    </div>
  );
};

export default DentalTriagePage;
